/**
 * Where to land after the user picks a different active school.
 *
 * Every id-bearing dashboard route is school-owned — an event, order, form,
 * page or school-user id belongs to exactly one school — so carrying the URL
 * across a switch guarantees the next read 404s against the school just
 * selected. Whatever that page does with a missing record is then the first
 * thing the admin sees, which is how switching school while viewing an event
 * put a full-screen "Something went wrong" on the demo.
 *
 * Truncating the path at its first id-shaped segment lands them on that
 * section's list for the new school. Routes with no id-shaped segment (lists,
 * settings, dashboards) carry no school-owned identifier in the URL and stay
 * put — switching school there is exactly the "show me the same view for a
 * different school" gesture the switcher is for.
 */

/**
 * Does this path segment look like a generated record id rather than a static
 * route segment?
 *
 * Covers the three id shapes the platform mints — cuid/cuid2 (a long unbroken
 * lowercase-alphanumeric run), uuid, and bare numeric ids. The 16-character
 * floor on the alphanumeric case is what keeps static segments out: every
 * static route segment in the app is a short word or a hyphenated phrase, and
 * hyphens disqualify a segment outright. A false negative is harmless (we just
 * leave the URL alone, the old behaviour); a false positive would bounce an
 * admin off a legitimate page, so the test is deliberately conservative.
 */
function looksLikeRecordId(segment: string): boolean {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true
  if (/^\d+$/.test(segment)) return true
  return /^[a-z0-9]{16,}$/.test(segment)
}

/**
 * Returns the path to navigate to after an active-school switch, or `null`
 * when the current path is school-agnostic and should be kept.
 *
 * @param pathname current `usePathname()` value
 */
export function pathAfterSchoolSwitch(pathname: string | null | undefined): string | null {
  if (!pathname) return null

  const segments = pathname.split('/').filter(Boolean)
  const idIndex = segments.findIndex(looksLikeRecordId)
  if (idIndex === -1) return null

  const kept = segments.slice(0, idIndex)
  return kept.length ? `/${kept.join('/')}` : '/'
}
